import * as Sentry from '@sentry/react';
import config from '@app/config';

export default class WebSentryLogger implements ExceptionLogger {
  private isProd: boolean;
  private static isInitialized = false;
  private url: string;

  constructor(url: string, version: string, isProd: boolean = false) {
    if (!WebSentryLogger.isInitialized) {
      Sentry.init({
        dsn: url,
        sendDefaultPii: true,
        environment: isProd ? 'production' : 'development',
        integrations: [
          Sentry.browserTracingIntegration(),
          Sentry.consoleLoggingIntegration({ levels: ['log', 'error', 'warn', 'info'] }),
        ],
        enableLogs: true,
        tracesSampleRate: 1.0,
        release: version,
      });
      WebSentryLogger.isInitialized = true;
    }
    this.isProd = isProd;
    this.url = url;
  }

  async init(): Promise<void> {
    this.event('SentryLogger initialized', {
      environment: this.isProd ? 'production' : 'development',
      timestamp: new Date().toISOString(),
      url: this.url,
    });
  }

  setUserInfo(userId: string, userInfo: any): void {
    Sentry.setUser({ userId, ...userInfo });
  }

  setTag(name: string, value: string | number): void {
    Sentry.setTags({ [name]: value });
  }

  identify(id: string): void {
    Sentry.setExtras({
      userId: id,
    });
  }

  exception(error: Error, _errorString: string, additionalData?: object): void {
    try {
      Sentry.withScope((scope) => {
        additionalData && scope.setExtra('additionalData', additionalData);
        Sentry.captureException(error);
      });
    } catch (e: unknown) {
      // Do nothing
    }
  }

  event(name: string, additionalData?: object): void {
    Sentry.addBreadcrumb({
      category: 'event',
      message: name,
      data: { ...additionalData },
    });

    Sentry.captureMessage(`Event: ${name}`, {
      level: 'info',
      extra: additionalData as Record<string, any>,
    });

    if (!this.isProd && config.debug) {
      console.log('%cWebSentryLogger', 'color: red; font-size: x-large; font-weight: bold;', name, additionalData);
    }
  }

  log(message: string, additionalData?: object): void {
    Sentry.captureMessage(message, {
      level: 'info',
      ...additionalData,
    });
  }

  currentView(viewName: string, additionalData?: object): void {
    this.event('navigation', { screenName: viewName, ...additionalData });
  }
}
